import React, { useState, useEffect } from "react";
import {
  cardStyles,
  profileCircleStyles,
  nameStyles,
} from "../../styles/components/UserProfile/UserMetaCard.styles";
import { UserData } from "../UserProfiles";
import { Modal } from "../../components/ui/modal";
import axios from "axios";


interface UserMetaCardProps {
  userData: UserData | null;
  setUserData: React.Dispatch<React.SetStateAction<UserData | null>>;
}

const UserMetaCard = ({ userData, setUserData }: UserMetaCardProps) => {
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [selectedProfileImage, setSelectedProfileImage] = useState<string | null>(null);

  // Listen for profile update events to refresh userData
  useEffect(() => {
    const handleProfileUpdate = async () => {
      const userId = sessionStorage.getItem("user_id");
      if (!userId) return;

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/profile/show`,
          { user_id: userId },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status === 200) {
          setUserData(response.data.data); // Update parent state with new user data
        }
      } catch (error) {
        console.error("Error fetching updated user data:", error);
      }
    };

    window.addEventListener("profileUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, [setUserData]);

  const getInitials = (first?: string, last?: string) => {
    return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
  };

  const handleImageModalOpen = (image: string) => {
    setSelectedProfileImage(image || "https://via.placeholder.com/150");
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedProfileImage(null);
  };

  return (
    <div className={cardStyles}>
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
          {userData?.profile_image ? (
            <>
              <img
                src={`${userData.profile_image}?t=${new Date().getTime()}`}
                alt="Profile"
                className={`${profileCircleStyles} object-cover rounded-full cursor-pointer`}
                onClick={() =>
                  handleImageModalOpen(
                    userData.profile_image || "https://via.placeholder.com/150"
                  )
                }
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const nextSibling = e.currentTarget.nextSibling;
                  if (nextSibling instanceof HTMLElement) {
                    nextSibling.style.display = "flex";
                  }
                }}
              />
              <div className={`${profileCircleStyles} hidden`}>
                {getInitials(userData?.first_name, userData?.last_name)}
              </div>
            </>
          ) : (
            <div className={profileCircleStyles}>
              {getInitials(userData?.first_name, userData?.last_name)}
            </div>
          )}

          <div className="order-3 xl:order-2">
            <h4 className={nameStyles}>
              {userData?.first_name} {userData?.last_name}
            </h4>
          </div>
        </div>
      </div>

      {selectedProfileImage && (
        <Modal
          isOpen={isImageModalOpen}
          onClose={handleImageModalClose}
          className="w-[70%] max-w-lg bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl mx-auto text-center"
        >
          <div className="flex justify-center">
            <img
              src={selectedProfileImage}
              alt="User Profile Enlarged"
              className="w-full h-auto max-h-[60vh] rounded object-contain"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserMetaCard;